import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Crown, Check, Loader2, Sparkles, Zap, CalendarCheck2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { useEdition } from "@/lib/edition";

const PERKS = [[Sparkles, "Advanced AI", "A smarter model for Ask Daybreyk, AI marking and revision packs"], [CalendarCheck2, "Test auto-revision", "Add a test and a full revision pack is built for you automatically"], [Zap, "Higher limits", "150 marked answers, 300 voice notes and 40 packs a day"]];
const CORP_PERKS = [[Sparkles, "Advanced AI", "A smarter model for Ask Daybreyk, your briefs and learning packs"], [CalendarCheck2, "Deadline prep", "Add a deadline and a prep plan is built for you automatically"], [Zap, "Higher limits", "300 voice notes and 40 learning packs a day"]];

export const PremiumCard = () => {
  const [p, setP] = useState(null);
  const [busy, setBusy] = useState("");
  const { corp } = useEdition();
  useEffect(() => { api.get("/premium").then((r) => setP(r.data ?? null)).catch(() => {}); }, []);
  const buy = async (plan) => {
    setBusy(plan);
    try { const { data } = await api.post("/payments/checkout", { plan, origin_url: window.location.origin }); window.location.href = data.checkout_url; }
    catch (e) { toast.error(formatApiError(e)); setBusy(""); }
  };
  if (!p) return null;
  const plans = Array.isArray(p.plans) ? p.plans : [];
  return (
    <article data-testid="premium-card" className="card min-w-0 p-5 sm:p-6">
      <div className="flex items-center gap-3">
        <span className="grid place-items-center w-10 h-10 shrink-0 rounded-xl bg-gold-400 text-black"><Crown className="w-5 h-5" /></span>
        <div><h3 className="font-semibold text-slate-900">Daybreyk {corp ? "Corporate " : ""}Premium</h3>
          <p data-testid="premium-status" className="text-sm text-slate-500">{p.premium ? (p.granted ? "Active, given free by an admin." : p.until ? `Active until ${new Date(p.until).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}.` : "Active.") : "Optional. Everything else in Daybreyk stays free."}</p></div>
      </div>
      <ul className="mt-4 space-y-2">{(corp ? CORP_PERKS : PERKS).map(([I, t, d]) => <li key={t} className="flex gap-2.5 text-sm"><I className="w-4 h-4 mt-0.5 shrink-0 text-gold-300" /><span><b className="text-slate-900">{t}</b> <span className="text-slate-600">{d}</span></span></li>)}</ul>
      {!p.premium && (
        <div className="mt-4 grid grid-cols-2 gap-2">
          {plans.map((pl) => (
            <button key={pl.id} data-testid={`premium-buy-${pl.id}`} onClick={() => buy(pl.id)} disabled={Boolean(busy)} className={`rounded-2xl border p-3 text-left transition-colors hover:border-gold-400 disabled:opacity-60 ${pl.id === "premium_yearly" ? "border-gold-400 bg-gold-400/10" : "border-slate-200"}`}>
              <span className="block text-xs font-bold text-slate-500">{pl.label}{pl.id === "premium_yearly" && " · save 37%"}</span>
              <span className="mt-0.5 flex items-center gap-1.5 font-display text-2xl text-slate-900">{busy === pl.id && <Loader2 className="w-4 h-4 animate-spin" />}{pl.price}<span className="text-sm font-sans text-slate-500">/{pl.per}</span></span>
            </button>
          ))}
        </div>
      )}
    </article>
  );
};

export default function PaymentResult() {
  const [params] = useSearchParams();
  const { setUser } = useAuth();
  const sid = params.get("session_id");
  const [state, setState] = useState(sid ? "checking" : "cancelled");
  useEffect(() => {
    if (!sid) return undefined;
    let n = 0;
    const t = setInterval(async () => {
      n += 1;
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (data?.payment_status === "paid") { clearInterval(t); setState("paid"); const me = await api.get("/auth/me"); setUser(me.data); }
        else if (["expired", "failed"].includes(data?.payment_status) || n > 20) { clearInterval(t); setState("failed"); }
      } catch { if (n > 20) { clearInterval(t); setState("failed"); } }
    }, 2000);
    return () => clearInterval(t);
  }, [sid, setUser]);
  const msg = { checking: ["Confirming your payment…", "This takes a few seconds."], paid: ["You're on Premium", "Thanks for supporting Daybreyk. Your Premium features are on."], failed: ["Payment not confirmed", "No money was taken if the payment didn't finish. You can try again from Settings."], cancelled: ["Payment cancelled", "No problem. Nothing was charged."] }[state];
  return (
    <main data-testid="payment-result-page" className="mx-auto max-w-lg px-4 py-16 text-center">
      <div className="card p-8">
        {state === "checking" ? <Loader2 className="w-10 h-10 mx-auto animate-spin text-gold-300" /> : state === "paid" ? <Check className="w-10 h-10 mx-auto text-emerald-400" /> : <Crown className="w-10 h-10 mx-auto text-slate-400" />}
        <h1 data-testid="payment-result-title" className="mt-4 font-display text-4xl text-slate-900">{msg[0]}</h1>
        <p className="mt-2 text-slate-600">{msg[1]}</p>
        <Link to="/connections" data-testid="payment-back" className="mt-6 inline-flex rounded-full bg-gold-400 px-6 py-3 text-sm font-bold text-black">Back to Settings</Link>
      </div>
    </main>
  );
}
